import { useRef, useState } from "react";
import { createDesignReviewRequest } from "../../api/design";
import { useNavigate, useParams } from "react-router-dom";
import ApprovalLine from "../../components/approval/ApprovalLine";

export default function DesignReviewForm({ onSuccess }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState(null);
  const { id: projectId } = useParams();
  const approvalRef = useRef();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("project", projectId);
    formData.append("title", title);
    formData.append("category", category);
    formData.append("discipline", discipline);
    formData.append("comment", comment);
    if (attachment) formData.append("attachment", attachment);

    try {
      const res = await createDesignReviewRequest(formData);
      const dns = res?.data?.dns;
      if (!dns) throw new Error("DNS 생성 실패");

      await approvalRef.current?.save(dns);

      if (onSuccess) onSuccess();
      setTitle("");
      setCategory("");
      setDiscipline("");
      setComment("");
      setAttachment(null);
      
      alert("도면 검토 요청이 등록되었습니다.");
      navigate(`/project/${projectId}/design/request`);
    } catch (err) {
      console.error("검토 요청 등록 실패:", err);
      alert("요청 등록 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 + 저장 버튼 */}
        <div className="bg-white rounded-lg p-3 border min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">도면 검토 요청</h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate">새 검토 요청 등록</p>
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn-save flex-shrink-0"
            >
              저장
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* 기본 정보 */}
          <div className="bg-white rounded-lg border overflow-hidden min-w-0">
            <div className="bg-gray-50 px-3 py-2 border-b">
              <div className="text-sm font-medium text-gray-900">기본 정보</div>
            </div>
            <div className="p-3 space-y-3">
              {/* 제목 */}
              <div>
                <label htmlFor="title" className="text-xs text-gray-500 mb-1 block">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="예: 철근 상세도 검토 요청"
                  required
                />
              </div>

              {/* 카테고리, 공종 (2열 그리드) */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="category" className="text-xs text-gray-500 mb-1 block">
                    카테고리
                  </label>
                  <input
                    id="category"
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="도면 / 사양서"
                  />
                </div>
                <div>
                  <label htmlFor="discipline" className="text-xs text-gray-500 mb-1 block">
                    공종
                  </label>
                  <select
                    id="discipline"
                    value={discipline}
                    onChange={(e) => setDiscipline(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">선택</option>
                    <option value="ARCH">건축</option>
                    <option value="STRU">구조</option>
                    <option value="ELEC">전기</option>
                    <option value="MECH">설비</option>
                    <option value="CIVIL">토목</option>
                    <option value="ETC">기타</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* 결재선 */}
          <div className="bg-white rounded-lg border overflow-hidden min-w-0">
            <div className="bg-gray-50 px-3 py-2 border-b">
              <div className="text-sm font-medium text-gray-900">결재선</div>
            </div>
            <div className="p-3">
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                <ApprovalLine ref={approvalRef} />
              </div>
            </div>
          </div>

          {/* 검토 의견 */}
          <div className="bg-white rounded-lg border overflow-hidden min-w-0">
            <div className="bg-gray-50 px-3 py-2 border-b">
              <div className="text-sm font-medium text-gray-900">검토 의견</div>
            </div>
            <div className="p-3">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full h-28 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="검토 요청 내용을 입력하세요"
              />
            </div>
          </div>

          {/* 첨부파일 */}
          <div className="bg-white rounded-lg border p-3 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-900">첨부파일</div>
            </div>
            <div>
              <input
                type="file"
                onChange={(e) => setAttachment(e.target.files[0])}
                className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {attachment && (
                <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                  선택된 파일: {attachment.name}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
